/**
 * Prices in cents, always whole numbers. Read as a menu writes them („5,90“, „5,90 €“, „12“) or as a
 * number of euros from a spreadsheet cell (5.9).
 */

export const MAX_PRICE = 1_000_000; // 10.000 € in cents

/** Cents from what someone typed or a cell holds; empty is no price. */
export function parsePrice(raw: unknown): { value: number | null } | { error: string } {
  if (raw === null || raw === undefined) return { value: null };
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || raw < 0 || raw * 100 > MAX_PRICE) return { error: `Preis „${raw}“ passt nicht` };
    return { value: Math.round(raw * 100) };
  }
  const text = String(raw).trim().replace(/\s*(€|eur|euro|euros)\s*$/i, "").replace(/^(€|eur)\s*/i, "").replace(/\s+/g, "");
  if (text === "" || text === "-" || text === "–") return { value: null };
  // „1.234,50“ and „1234,5“ as in Germany; „12.50“ with a point only when it has one or two decimals.
  let normal: string;
  if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(text)) normal = text.replace(/\./g, "").replace(",", ".");
  else if (/^\d+(,\d{1,2})?$/.test(text)) normal = text.replace(",", ".");
  else if (/^\d+\.\d{1,2}$/.test(text)) normal = text;
  else if (/^\d+,-$/.test(text)) normal = text.slice(0, -2);
  else return { error: `Preis „${String(raw)}“ ist keine Zahl` };
  const cents = Math.round(Number(normal) * 100);
  if (!Number.isFinite(cents) || cents > MAX_PRICE) return { error: `Preis „${String(raw)}“ passt nicht` };
  return { value: cents };
}

/** „5,90“: two decimals, no sign, for tables and inputs. */
export const plainPrice = (cents: number | null): string => (cents === null ? "" : (cents / 100).toFixed(2).replace(".", ","));

/** „5,90 €“ */
export const euro = (cents: number | null): string => (cents === null ? "" : `${plainPrice(cents)} €`);

/** Euros as a number for a spreadsheet cell. */
export const euroCell = (cents: number | null): number | null => (cents === null ? null : Math.round(cents) / 100);
